import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, ChevronLeft } from "lucide-react";
import Seo from "@/components/Seo";

const PATH = "/blog/what-is-a-destination-management-company";
const TITLE = "What Is a Destination Management Company (DMC)?";
const DESCRIPTION =
  "A DMC handles all ground logistics for corporate and group travel. Learn what a DMC does, when you need one, and how to choose the right one.";
const PUBLISHED_ISO = "2026-02-16";
const PUBLISHED_LABEL = "Feb 16, 2026";
const AUTHOR = "Trawelldmc Team";
const IMAGE =
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=webp&w=1600&q=80";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Blog", path: "/blog" },
  { name: TITLE, path: PATH },
];

const ARTICLE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: TITLE,
  description: DESCRIPTION,
  image: IMAGE,
  datePublished: PUBLISHED_ISO,
  dateModified: PUBLISHED_ISO,
  author: { "@type": "Organization", name: AUTHOR, url: "https://www.trawelldmc.com" },
  publisher: {
    "@type": "Organization",
    name: "Trawelldmc",
    logo: {
      "@type": "ImageObject",
      url: "https://www.trawelldmc.com/trawelldmc-logo-hd.png",
    },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.trawelldmc.com${PATH}` },
};

export default function BlogWhatIsADmc() {
  return (
    <div data-testid="blog-what-is-dmc">
      <Seo
        path={PATH}
        title="What Is a Destination Management Company (DMC)? | Trawelldmc"
        description={DESCRIPTION}
        type="article"
        image={IMAGE}
        breadcrumb={BREADCRUMB}
        extraJsonLd={ARTICLE_JSONLD}
      />

      {/* HERO */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8"
            data-testid="blog-back"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Blog
          </Link>
          <div className="overline !text-[#FF5238] mb-5">DMC Basics</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08]">
            {TITLE}
          </h1>
          <div className="mt-8 flex items-center gap-5 text-sm text-white/60">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {PUBLISHED_LABEL}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              6 min read
            </span>
            <span>By {AUTHOR}</span>
          </div>
        </div>
      </section>

      {/* HERO IMAGE */}
      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 -mt-10 mb-12">
          <img
            src={IMAGE}
            alt="Corporate conference event managed by a DMC — Trawelldmc destination management experts"
            width="1600"
            height="900"
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover rounded-md shadow-xl"
          />
        </div>
      </section>

      {/* BODY */}
      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24 prose-trawell">
        <p className="text-xl text-slate-700 leading-relaxed font-light">
          If you've ever planned a corporate offsite, a multi-day conference,
          or an incentive trip for 200 of your top performers, you've
          probably wondered whether you need a Destination Management Company
          (DMC) — and what one actually does. This guide answers that
          question end-to-end.
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5">
          1. What is a DMC?
        </h2>
        <p className="text-slate-700 leading-relaxed">
          A Destination Management Company is a professional services
          organisation with deep local expertise in a specific destination
          (or, in our case, in seven destinations across Dubai and Southeast
          Asia). Their job is to handle every aspect of ground logistics,
          venue management, and creative programming for corporate, MICE
          (Meetings, Incentives, Conferences, Exhibitions), and group leisure
          travel.
        </p>
        <p className="text-slate-700 leading-relaxed mt-4">
          Think of a DMC as your local team in a foreign country — the people
          who own relationships with hotels, venues, transport operators,
          guides, F&B vendors, and AV companies, and who can execute a
          1,000-person event in a city you've never visited.
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5">
          2. What services does a DMC provide?
        </h2>
        <p className="text-slate-700 leading-relaxed">
          A full-service DMC handles eight overlapping categories of work:
        </p>
        <ul className="text-slate-700 leading-relaxed mt-4 space-y-2 list-disc pl-6">
          <li><strong>Venue sourcing & contracting</strong> — convention centres, hotel ballrooms, integrated resorts, harbour venues, hotel buyouts</li>
          <li><strong>Accommodation management</strong> — hotel blocks, multi-property arrangements, rooming lists, VIP allocations</li>
          <li><strong>Ground transport</strong> — airport transfers, coach fleets, VIP cars, helicopter or yacht transfers</li>
          <li><strong>Event production & AV</strong> — stage design, lighting, audio, LED walls, simultaneous interpretation, content production</li>
          <li><strong>F&B coordination</strong> — themed gala dinners, branded F&B, dietary compliance (halal, kosher, vegetarian)</li>
          <li><strong>Creative programming</strong> — team-building, cultural experiences, signature incentive moments</li>
          <li><strong>Visa & travel documentation</strong> — group visa coordination, immigration support</li>
          <li><strong>On-ground operations</strong> — 24/7 programme manager, real-time logistics support, contingency planning</li>
        </ul>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5">
          3. Who uses DMCs?
        </h2>
        <p className="text-slate-700 leading-relaxed">
          Three buyer profiles use DMCs most heavily. <strong>Corporate
          travel teams</strong> use DMCs for any group programme over ~20
          pax where in-house resources fall short — board offsites,
          leadership retreats, multi-city sales kick-offs. <strong>MICE
          buyers</strong> (in-house event managers, agency planners, PCOs)
          use DMCs to deliver large-scale conferences, exhibitions, and
          dealer congresses. <strong>Incentive buyers</strong> (HR teams,
          sales operations) use DMCs to design and operate reward trips for
          top performers — bespoke programmes where the bar is "memorable
          experiences your delegates will talk about for years."
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5">
          4. How is a DMC different from a travel agency?
        </h2>
        <p className="text-slate-700 leading-relaxed">
          A travel agency books flights and hotels. A DMC builds and operates
          programmes. The difference shows up in scope, accountability, and
          creative capability. A travel agency typically operates at the
          individual-booking level. A DMC owns end-to-end accountability for
          a programme — venue, AV, F&B, transport, creative production, and
          on-ground operations all under one contract.
        </p>
        <p className="text-slate-700 leading-relaxed mt-4">
          The other key difference is local expertise. A travel agency in
          London books a hotel in Dubai through a global distribution system.
          A Dubai-headquartered DMC like Trawelldmc has direct relationships
          with the Dubai hotels — better rates, better service, better
          flexibility on rooming lists and amenities. The same applies in
          every destination we operate.
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5">
          5. When should you hire a DMC?
        </h2>
        <p className="text-slate-700 leading-relaxed">
          You should engage a DMC when any of the following are true: your
          group is 20+ pax; you need event production beyond just travel
          (stage, AV, themed F&B); you don't have local relationships in the
          destination; your programme spans multiple cities; or you need
          24/7 on-ground support during the event. For smaller individual
          business trips or single-city corporate bookings without event
          production, a travel agency is usually sufficient.
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5">
          6. How to evaluate and choose a DMC
        </h2>
        <p className="text-slate-700 leading-relaxed">
          Use four criteria. First, <strong>operating depth</strong> — how
          many years have they worked the destination, and how many
          comparable-scale programmes have they delivered? Second,
          <strong> end-to-end capability</strong> — venue, accommodation,
          production, transport and creative under one contract beats
          coordinating five vendors. Third, <strong>references from
          comparable buyers</strong> — corporates of your scale, in your
          industry, on similar-scope events. Fourth, <strong>a named senior
          account manager</strong> who owns your programme from brief to
          reconciliation. Avoid junior-staffed agencies that hand off
          execution to subcontractors.
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5">
          7. Conclusion
        </h2>
        <p className="text-slate-700 leading-relaxed">
          A DMC is the operating partner that turns your corporate or
          incentive travel brief into a polished, delivered programme.
          Trawelldmc has spent 20+ years operating across seven destinations
          — Dubai, Singapore, Malaysia, Thailand, Hong Kong, Vietnam and the
          Philippines — and we've served over 140,000 travellers. If you're
          planning a programme in any of those markets, we'd love to talk.
          Explore our dedicated <Link to="/dubai-dmc" className="text-[#FF5238] underline">Dubai DMC</Link> page, our <Link to="/mice-services" className="text-[#FF5238] underline">MICE services</Link> overview, or our <Link to="/incentive-travel" className="text-[#FF5238] underline">incentive travel programmes</Link>.
        </p>

        <div className="mt-12 p-8 bg-[#FDFCF8] border-l-4 border-[#FF5238] rounded-r-md">
          <div className="overline mb-3">Next step</div>
          <h3 className="font-heading text-2xl font-medium text-[#0B192C] tracking-tight">
            Ready to brief a programme?
          </h3>
          <p className="text-slate-600 mt-3 leading-relaxed">
            Submit an RFP or talk to a senior account manager. We respond
            within 24 hours.
          </p>
          <Link
            to="/inquiry"
            className="btn-primary mt-6 inline-flex"
            data-testid="article1-cta"
          >
            Request a proposal <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </article>
    </div>
  );
}
